import clsx from 'clsx';
import {
  forwardRef,
  Fragment,
  type PropsWithChildren,
  Ref,
  useImperativeHandle,
  useRef,
} from 'react';

import { MeterBarLayoutTopRightSlot } from './components/MeterBarLayoutTopRightSlot.js';
import { useComputedSegmentSize } from './hooks/useComputedSegmentSize.js';
import {
  chartContainer,
  container,
  containerWrapper,
  labelContainer,
  legendContainer,
  maxContainer,
  minContainer,
  thresholdsContainer,
} from './MeterBarLayout.sty.js';
import { Chart } from './slots/Chart.js';
import { Icon } from './slots/Icon/Icon.js';
import { Label } from './slots/Label/Label.js';
import { Legend } from './slots/Legend.js';
import { Max } from './slots/Max/Max.js';
import { Min } from './slots/Min/Min.js';
import { Providers } from './slots/Providers.js';
import { Thresholds } from './slots/Thresholds.js';
import { Value } from './slots/Value/Value.js';
import { extractSlots } from './utils/extract-slots.js';
import { useBehavioralTrackingProps } from '../../../../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../../../../core/hooks/useMergeRefs.js';
import type { AriaLabelingProps } from '../../../../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../../../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../../../../core/types/data-props.js';
import type { MaskingProps } from '../../../../../core/types/masking-props.js';
import type { StylingProps } from '../../../../../core/types/styling-props.js';
import { Flex } from '../../../../../layouts/flex/Flex.js';
import { Grid } from '../../../../../layouts/grid/Grid.js';
import { LoaderOverlay } from '../../../../core/components/loader-overlay/LoaderOverlay.js';
import { useTooltipImperativeHandle } from '../../../../core/components/overlay-tooltip/hooks/useTooltipImperativeHandle.js';
import type { ComponentWithName } from '../../../../core/types/component-with-name.js';
import { coerceSizeValue } from '../../../../core/utils/coerceSizeValue.js';
import type { MultiMeterBarChartConsumerConfig } from '../../../multi-meter-bar/types/multi-meter-bar-chart-config.js';
import type { MeterBarChartConsumerConfig } from '../../../single-meter-bar/types/meter-bar-chart-config.js';
import {
  DEFAULT_HEIGHT,
  DEFAULT_WIDTH,
  METER_BAR_SIZE,
} from '../../constants.js';
import { useMeterBarChartLoading } from '../../context/MeterBarChartLoadingContext.js';
import { useMeterBarChartToolbar } from '../../hooks/useMeterBarChartToolbar.js';
import { consumerLoadingStyle } from '../../styles.sty.js';
import type {
  MeterBarChartElementRef,
  MeterBarSize,
} from '../../types/meter-bars.js';
import { MeterBarSizeProviders } from '../meter-bar-providers/MeterBarSizeProviders.js';

export interface MeterBarLayoutProps
  extends StylingProps,
    MaskingProps,
    DataTestId,
    AriaLabelingProps,
    BehaviorTrackingProps {
  /**
   * The size that applies to the value, icon, and label.
   * @defaultvalue 'size16'
   */
  size: MeterBarSize;
  /**
   * The chart width. A valid CSS width is expected.
   * @defaultValue 100%
   */
  width?: number | string;
  /**
   * The chart height. A valid CSS height is expected.
   * @defaultValue 300px
   */
  height?: number | string;
  /**
   * The component name used for internal tracking purposes.
   */
  'data-dt-component': string;
  /**
   * A ref object that exposes imperative methods for the meter bar chart element.
   */
  userRef?: Ref<MeterBarChartElementRef>;
  /**
   * Configuration passed from the consumer chart component (e.g. MeterBarChart or MultiMeterBarChart).
   */
  consumerConfig?:
    | MeterBarChartConsumerConfig
    | MultiMeterBarChartConsumerConfig;
}

/**
 * Component responsible for defining the layout of the meter bars.
 * The layout is divided in the labels area (icon, label and value), chart area and legend area.
 */
export const MeterBarLayoutRoot = forwardRef<
  HTMLDivElement,
  PropsWithChildren<MeterBarLayoutProps>
>((props, forwardedRef) => {
  const {
    width,
    height,
    size,
    children,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId,
    'data-dt-component': dataDtComponent,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
    userRef,
    consumerConfig = {},
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);

  const { label, min, max, value, chart, icon, legend, thresholds, providers } =
    extractSlots(children);

  const [behavioralTrackingProps] = useBehavioralTrackingProps(props);
  const loading = useMeterBarChartLoading();
  const { isToolbarEnabled, modifyHoverState, toolbarRef } =
    useMeterBarChartToolbar();
  const tooltipActions = useTooltipImperativeHandle();

  const ref = useMergeRefs([containerRef, forwardedRef]);

  useImperativeHandle(userRef, () => {
    return {
      element: containerRef.current,
      getConfig: () => JSON.stringify(consumerConfig),
      ...tooltipActions,
    };
  });

  const hasAutoSize = size === METER_BAR_SIZE.Auto;
  const gridTemplateRows = hasAutoSize ? 'auto 1fr auto auto auto' : 'auto';

  const {
    segmentSize: { height: segmentHeight, width: segmentWidth },
    labelRef,
    minLabelRef,
    maxLabelRef,
    plotAreaRef,
    legendRef,
  } = useComputedSegmentSize();

  const chartWidth = coerceSizeValue(width) ?? DEFAULT_WIDTH;
  const chartHeight = coerceSizeValue(height) ?? DEFAULT_HEIGHT;
  const ExternalProviders = providers ? providers.Component : Fragment;
  const externalProvidersProps = providers ? providers.props : {};

  return (
    <div
      {...behavioralTrackingProps}
      className={clsx(className, containerWrapper)}
      style={{
        width: chartWidth,
        height: chartHeight,
        ...style,
      }}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-dt-component={dataDtComponent}
      ref={ref}
    >
      <div
        className={container({ showLoading: loading, autoSize: hasAutoSize })}
      >
        <Grid
          className={container({ autoSize: hasAutoSize })}
          gridTemplateAreas="
            'label label label value'
            'chart chart chart chart'
            'thresholds thresholds thresholds thresholds'
            'min min max max'
            'legend legend legend legend'"
          gridTemplateColumns="25% 25% 25% 25%"
          gridTemplateRows={gridTemplateRows}
          rowGap={0}
          columnGap={0}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-details={ariaDetails}
          role="img"
          onMouseEnter={() => modifyHoverState(true)}
          onMouseLeave={() => modifyHoverState(false)}
          onFocus={() => modifyHoverState(true)}
          onBlur={() => modifyHoverState(false)}
        >
          <MeterBarSizeProviders
            size={size}
            chartWidth={containerRef.current?.clientWidth ?? 0}
            chartHeight={containerRef.current?.clientHeight ?? 0}
            segmentWidth={segmentWidth}
            segmentHeight={segmentHeight}
            data-dtrum-allow={dataDtrumAllow}
            data-dtrum-mask={dataDtrumMask}
          >
            <ExternalProviders {...externalProvidersProps}>
              {(label || icon) && (
                <Flex
                  ref={labelRef}
                  alignItems="center"
                  alignSelf="center"
                  className={labelContainer}
                >
                  {icon}
                  {label}
                </Flex>
              )}
              {(isToolbarEnabled || value) && (
                <MeterBarLayoutTopRightSlot
                  value={value}
                  isToolbarEnabled={isToolbarEnabled}
                  ref={toolbarRef}
                />
              )}
              <div className={chartContainer} ref={plotAreaRef}>
                {chart}
              </div>
              {thresholds && (
                <div className={thresholdsContainer}>{thresholds}</div>
              )}
              {min && (
                <div ref={minLabelRef} className={minContainer}>
                  {min}
                </div>
              )}
              {max && (
                <div ref={maxLabelRef} className={maxContainer}>
                  {max}
                </div>
              )}
              {legend && (
                <div ref={legendRef} className={legendContainer}>
                  {legend}
                </div>
              )}
              {loading ? (
                <LoaderOverlay
                  gridArea="1/1/2/2"
                  consumerStyle={consumerLoadingStyle}
                />
              ) : null}
            </ExternalProviders>
          </MeterBarSizeProviders>
        </Grid>
      </div>
    </div>
  );
});

(
  MeterBarLayoutRoot as ComponentWithName<typeof MeterBarLayoutRoot>
).displayName = 'MeterBarLayoutRoot';

/** Slot component for meter bar */
export const MeterBarLayout = Object.assign(MeterBarLayoutRoot, {
  Label,
  Min,
  Max,
  Chart,
  Value,
  Icon,
  Providers,
  Legend,
  Thresholds,
});
